import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { FinanceAccount, IAPIFinanceAccount } from 'src/model/financeAccount.model';

interface FinanceAccountOptions {
	id?: number;
	code?: string;
	disabled?: boolean;
	businessId?: number;
}

@Injectable({ providedIn: 'root' })
class FinanceAccountAdapter {
	adapt(item: any): FinanceAccount {
		return new FinanceAccount(
			item.code,
			item.bank,
			item.number.replace('-', ''),
			item.description,
			item.directors,
			item.createdOn,
			item.createdBy,
			item.disabled,
			item.id,
			item.businessIds
		);
	}

	toApiModel(fa: FinanceAccount): IAPIFinanceAccount {
		return {
			code: fa.Code,
			bank: fa.Bank,
			number: fa.AccNumber,
			description: fa.Description,
			directors: fa.Directors,
		};
	}
}

@Injectable({ providedIn: 'root' })
export class FinanceAccountService {
	private api = inject(RestApiService);
	private constructApi = inject(ConstructAPI);
	private adapter = inject(FinanceAccountAdapter);

	getAllFinanceAccounts(o?: FinanceAccountOptions): Promise<FinanceAccount[]> {
		0;
		return new Promise<FinanceAccount[]>((resolve, reject) => {
			this.api
				.get<FinanceAccount[]>(this.constructApi.getAllFinanceAccount(o?.id, o?.code, o?.disabled, o?.businessId))
				.pipe(map(data => data.map(d => this.adapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					},
				});
		});
	}

	getBusinessFinanceAccounts(businessId: number): Promise<FinanceAccount[]> {
		0;
		return new Promise<FinanceAccount[]>((resolve, reject) => {
			this.api
				.get<FinanceAccount[]>(this.constructApi.getBusinessFinanceAccounts(businessId))
				.pipe(map(data => data.map(d => this.adapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					},
				});
		});
	}

	postAddFinanceAccount(fa: FinanceAccount): Promise<FinanceAccount> {
		return new Promise<FinanceAccount>((resolve, reject) => {
			this.api.post<FinanceAccount>(this.constructApi.getAddFinanceAccount(), this.adapter.toApiModel(fa)).subscribe({
				next: data => resolve(this.adapter.adapt(data)),
				error: error => {
					this.api.handleError(error);
					reject(error);
				},
			});
		});
	}

  putUpdateFinanceAccount(fa: FinanceAccount): Promise<FinanceAccount> {
    return new Promise<FinanceAccount>((resolve, reject) => {
      this.api.put<FinanceAccount>(this.constructApi.getFinanceAccountById(fa.Id), this.adapter.toApiModel(fa)).subscribe({
        next: data => resolve(this.adapter.adapt(data)),
        error: error => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

	disableFinanceAccount(accId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			this.api.post<FinanceAccount>(this.constructApi.getDisableFinanceAccount(accId)).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				},
			});
		});
	}

	linkBusinessToFinanceAccount(faId: number, businessId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			this.api.post<FinanceAccount>(this.constructApi.getLinkBusinessToFinanceAccount(faId, businessId)).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				},
			});
		});
	}

	unlinkBusinessToFinanceAccount(faId: number, businessId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			this.api.post<FinanceAccount>(this.constructApi.getUnlinkBusinessToFinanceAccount(faId, businessId)).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				},
			});
		});
	}
}
